import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Paginate } from '@shared/entities/paginate';
import { SFContact } from '@shared/entities/sf-contact';
import { ApiService, Pagination, PER_PAGE } from '@shared/services';
import { Observable, BehaviorSubject, switchMap, shareReplay, take, Subject, tap, of, map, catchError } from 'rxjs';

const LOG_PREFIX = '[Api][Contact] ';

@Injectable({
  providedIn: 'root'
})
export class SFContactService extends ApiService<SFContact> {
  private _selectedAccountIds$ = new BehaviorSubject<string[]>([]);
  private _refresh$: Subject<Pagination>;
  private _allContacts$: Observable<Paginate<SFContact>>;
  private _currentPaginator: Pagination;
  private _contactsCache = new Map<string, SFContact>();

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/sf-contacts';
    const detailUrl = '/sf-contacts';

    super(SFContact, masterUrl, detailUrl, undefined, 'sf-contact');
    this._currentPaginator = { page: 1, perPage: PER_PAGE };
    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this._allContacts$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }

        return this._selectedAccountIds$.pipe(
          take(1),
          switchMap((accounts) => {
            if (accounts && accounts.length > 0) {
              this.setUrlParameter('accountIds', JSON.stringify(accounts.map((id: string) => id)));
            }
            return this.paginate(SFContact, pagination.page, pagination.perPage);
          })
        );
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ accounts', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ accounts error', err)
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh users');
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this._allContacts$.pipe(take(1));
  }

  searchValidContacts(keyword: string): Observable<SFContact[]> {
    this.logger.debug(LOG_PREFIX + 'searchAccounts', keyword);

    this.setUrlParameter('keyword', keyword);
    this.setUrlParameter('onlyValid', 'true');
    this.setUrlParameter('includeAdmin', 'false');

    return this.paginate(SFContact, 1, 10).pipe(
      map((response) => response.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'searchAccounts error', error);
        return of([]);
      })
    );
  }

  searchContact(keyword: string): Observable<SFContact[]> {
    this.logger.debug(LOG_PREFIX + 'searchAccounts', keyword);

    this.setUrlParameter('keyword', keyword);

    return this.paginate(SFContact, 1, 10).pipe(
      map((response) => response.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'searchAccounts error', error);
        return of([]);
      })
    );
  }

  searchContactExcludeActivated(keyword: string): Observable<SFContact[]> {
    this.logger.debug(LOG_PREFIX + 'searchContactExcludeActivated', keyword);

    this.setUrlParameter('keyword', keyword);
    this.setUrlParameter('showActivated', 'false');

    return this.paginate(SFContact, 1, 10).pipe(
      map((response) => response.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'searchContactExcludeActivated error', error);
        return of([]);
      })
    );
  }

  searchContactExceptAdmin(keyword: string): Observable<SFContact[]> {
    this.logger.debug(LOG_PREFIX + 'searchAccounts', keyword);

    this.setUrlParameter('keyword', keyword);
    this.setUrlParameter('includeAdmin', 'false');

    return this.paginate(SFContact, 1, 10).pipe(
      map((response) => response.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'searchAccounts error', error);
        return of([]);
      })
    );
  }

  searchContactById(contactId: string): Observable<SFContact | null> {
    if (this._contactsCache.has(contactId)) {
      return of(this._contactsCache.get(contactId) || null);
    }

    this.logger.debug(LOG_PREFIX + 'searchContactById', contactId);

    this.setUrlParameter('id', contactId);

    return this.paginate(SFContact, 1, 1).pipe(
      tap(() => this.removeUrlParameter('id')),
      map((response) => {
        const contact = response.data && response.data.length > 0 ? response.data[0] : null;
        if (contact) {
          this._contactsCache.set(contactId, contact);
        }
        return contact;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'searchContactById error', error);
        return of(null);
      })
    );
  }

  clearContactCache(): void {
    this._contactsCache.clear();
  }
}
